import { Controller } from "@hotwired/stimulus";
import * as d3 from "d3";

export default class extends Controller {
    static targets = ["buttonContainer", "button", "dropdown", "logo"]

    initialize() {
        this.colors = this.getColors();

        this.width = this.element.clientWidth + 15;
        this.init();

        window.addEventListener("resize", () => {
            this.deleteChart();
            this.width = this.element.clientWidth + 15;
            this.init();
        });
    }

    init() {
        this.setAttributes();
        this.setButtonWidth();
        this.logoTarget.style.transform = `scale(${this.width * 0.08 / 100})`;
        this.buttonContainerTarget.style.height = this.height + "px";
        this.createChart();
        this.setButtonTranslate();
    }

    setAttributes() {
        this.height = Math.min(350, this.width / 2) + 60;
        this.pieData = [1, 1, 1, 1];
        this.outerRadius = (this.height - 60) / 2 - 85;
        this.lineAngle = 75;
        this.buttonCoordinates = [];
        this.curve = d3.line().curve(d3.curveLinear);
    }

    getColors() {
        const colors = [];
        this.buttonTargets.forEach(buttonElement => {
            colors.push(buttonElement.dataset.color);
        });
        return colors;
    }

    setButtonWidth() {
        let maxButtonWidth = 0;
        this.buttonTargets.forEach(buttonElement => {
            buttonElement.style.width = null;
            maxButtonWidth = maxButtonWidth > buttonElement.clientWidth ? maxButtonWidth : buttonElement.clientWidth;
        });

        this.buttonTargets.forEach(buttonElement => {
            buttonElement.style.width = maxButtonWidth + 1 + "px";
        });

        this.dropdownTargets.forEach(dropdownElement => {
            dropdownElement.style.width = maxButtonWidth + 1 + "px";
        });
        this.maxButtonWidth = maxButtonWidth;
    }

    setButtonTranslate() {
        // Safety check: ensure buttonTargets exists and has elements
        if (!this.buttonTargets || this.buttonTargets.length === 0) {
            return;
        }
        
        for (let i = 0; i < this.buttonCoordinates.length && i < 4; i++) {
            const buttonCoordinate = this.buttonCoordinates[i];
            const buttonElement = this.buttonTargets[i];
            
            // Skip if button element doesn't exist
            if (!buttonElement) {
                continue;
            }
            
            let translateX = buttonCoordinate.x + this.width / 2;
            
            translateX -= buttonCoordinate.x < 0 ? this.maxButtonWidth - 42 : -85;
            
            if (translateX < 10) {
                translateX = 10;
            } else if (translateX > this.width - this.maxButtonWidth - 30 / 2 - 10) {
                translateX = this.width - this.maxButtonWidth - 30 / 2 - 10;
            }
            
            buttonElement.style.transform = `translate(${translateX}px, ${buttonCoordinate.y + this.height / 2 - 55}px)`;
        }
        
        if (this.buttonTargets.length >= 5) {
            const fifthButton = this.buttonTargets[4];
            if (fifthButton) {
                const centerX = this.width / 2 - this.maxButtonWidth / 2;
                const bottomY = this.height - 70 - 35;
                fifthButton.style.transform = `translate(${centerX}px, ${bottomY}px)`;
            }
        }
    }

    createChart() {
        const staleSVG = this.element.querySelector("svg[class='chart']");
        if (staleSVG) {
            staleSVG.remove();
        }

        this.svg = d3.create("svg")
            .attr("viewBox", [-this.width / 2, -this.height / 2 + 35, this.width, this.height])
            .attr("class", "chart");
            
        const arc = d3.arc()
            .innerRadius(0)
            .outerRadius(this.outerRadius);

        const pie = d3.pie().sort(null).value((d) => d);

        const g = this.svg.selectAll(".arc")
            .data(pie(this.pieData))
            .enter()
            .append("g")
            .attr("class", "arc");

        const labelr = this.outerRadius + 60;
        
        g.append("point")
            .attr("x", function (d) {
                let a = d.startAngle + (d.endAngle - d.startAngle) / 2 - Math.PI / 2;
                d.cx = Math.cos(a) * (labelr - 75);
                d.x = Math.cos(a) * (labelr - 20) * 2;
                return d.x;
            })
            .attr("y", function (d) {
                let a = d.startAngle + (d.endAngle - d.startAngle) / 2 - Math.PI / 2;
                d.cy = Math.sin(a) * (labelr - 75);
                d.y = Math.sin(a) * (labelr - 20);
                return d.y;
            })
            .attr("transform", "translate(0, 5)")
            .each(function (d) {
                d.sx = d.x - this.lineAngle;
                d.ox = d.x + this.lineAngle;
                d.sy = d.oy = d.y;
                this.buttonCoordinates.push({
                    x: d.sx,
                    y: d.sy
                });
            }.bind(this));

        g.append("path")
            .style("fill", "none")
            .attr("class", (d, i) => "stroke-" + this.colors[i])
            .style("stroke-width", 1.7)
            .attr("d", this.getLine.bind(this));

        g.append('path')
            .attr("d", arc)
            .attr("class", "arc")
            .attr("fill", "#F1F2F7");

        if (this.buttonTargets.length == 5) {
            const buttonAbsoluteY = this.height - 70 - 35;
            const fifthButtonY = buttonAbsoluteY - this.height / 2 + 35;
            this.svg.append("path")
                .style("fill", "none")
                .attr("class", "stroke-" + this.colors[4])
                .style("stroke-width", 1.7)
                .attr("d", this.getFifthButtonLine(fifthButtonY, this.outerRadius));
        }

        const logoScale = parseFloat(this.logoTarget.style.transform.replace("scale(", "").replace(")", ""));
        const logoBoundingBox = this.logoTarget.getBoundingClientRect();
        const rectWidth = logoBoundingBox.width * logoScale * 0.25;
        
        this.svg.append('rect')
            .attr("fill", "white")
            .attr("transform", `translate(${this.outerRadius}, -5)`)
            .attr("width", rectWidth)
            .attr("height", 20);

        this.svg.append('rect')
            .attr("fill", "white")
            .attr("transform", `translate(${-this.outerRadius - rectWidth}, -5)`)
            .attr("width", rectWidth)
            .attr("height", 20);

        this.element.appendChild(Object.assign(this.svg.node()));
    }

    deleteChart() {
        const existingSvg = this.element.querySelector("svg[class='chart']");
        if (existingSvg) {
            existingSvg.remove();
        }
    }

    getLine(d) {
        let points;
        if (d.cx > d.ox) {
            points = [[d.sx, d.sy], [d.ox, d.oy], [d.cx, d.cy]];
        } else {
            points = [[d.ox, d.oy], [d.sx, d.sy], [d.cx, d.cy]];
        }
        return this.curve(points);
    }
    
    getFifthButtonLine(buttonY, outerRadius) {
        const points = [[0, outerRadius], [0, buttonY]];
        return this.curve(points);
    }
}
